import type { ConnectedWallet } from '@privy-io/react-auth';
import { PrivyProvider } from '@privy-io/react-auth';
import { WagmiProvider } from '@privy-io/wagmi';
import * as React from 'react';
import { SnackbarProvider } from 'notistack';
import { ModalProvider } from 'mui-modal-provider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { config } from './wagmi';
import { privyAppId, privyConfig } from './privy';

/**
 * Pick the first Privy-connected wallet to set as active in wagmi.
 * Without this callback the adapter relies on wagmi's `reconnect()`,
 * which can silently fail for external wallets (e.g. MetaMask) after
 * a page refresh, leaving `useAccount()` disconnected even though
 * Privy holds a valid session with a linked wallet.
 */
function pickActiveWalletForWagmi({
  wallets,
}: {
  wallets: ConnectedWallet[];
}): ConnectedWallet | undefined {
  return wallets[0];
}

const queryClient = new QueryClient();

export function Providers({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  if (!privyAppId) {
    throw new Error(
      'Missing VITE_PRIVY_APP_ID. Please update your environment variables.',
    );
  }

  return (
    <SnackbarProvider
      anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
      classes={{ containerRoot: 'snackbarContainer' }}
    >
      <QueryClientProvider client={queryClient}>
        <PrivyProvider appId={privyAppId} config={privyConfig}>
          <WagmiProvider config={config} setActiveWalletForWagmi={pickActiveWalletForWagmi}>
            <ModalProvider>{mounted && children}</ModalProvider>
          </WagmiProvider>
        </PrivyProvider>
      </QueryClientProvider>
    </SnackbarProvider>
  );
}
